import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api, { asArray } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { getNotificationsLastSeenKey } from '../utils/notificationAlerts';

const TABS = [
  { to: '/signals', label: 'Signals' },
  { to: '/ai-signals', label: "Today's AI Signal" },
  { to: '/notifications', label: 'Notifications' },
];

/** True when there's an admin notification newer than the user last saw — drives the
 *  unread dot on the Notifications tab here and on the Signals page's bell icon. */
export function useUnreadNotifications() {
  const { user } = useAuth();
  const [hasUnread, setHasUnread] = useState(false);

  useEffect(() => {
    const check = () => {
      api.get('/notifications').then(res => {
        const list = asArray(res.data);
        if (list.length === 0) { setHasUnread(false); return; }
        const lastSeen = Number(localStorage.getItem(getNotificationsLastSeenKey(user)) || 0);
        const newest = new Date(list[0].createdAt).getTime();
        setHasUnread(newest > lastSeen);
      }).catch(() => {});
    };
    check();
    const onSeen = () => setHasUnread(false);
    window.addEventListener('sx-notifications-seen', onSeen);
    // A push arrived while the app is open (Android shows no popup then) — re-check
    window.addEventListener('sx-notification-pushed', check);
    return () => {
      window.removeEventListener('sx-notifications-seen', onSeen);
      window.removeEventListener('sx-notification-pushed', check);
    };
  }, [user]);

  return hasUnread;
}

/** Lets the user switch between the signal-browsing/notification pages directly from
 *  any one of them, instead of opening the account dropdown each time. Rendered at
 *  the top of WeeklySignals.jsx, AiSignals.jsx, and Notifications.jsx (the Signals
 *  page itself has icon buttons for the same jumps). */
export default function SignalTabsNav() {
  const location = useLocation();
  const hasUnread = useUnreadNotifications();

  return (
    <div className="flex flex-wrap gap-1 bg-slate-100 rounded-xl p-1 mb-4 w-fit">
      {TABS.map(t => (
        <Link
          key={t.to}
          to={t.to}
          className={`relative text-xs px-3 py-1.5 rounded-lg transition ${
            location.pathname === t.to
              ? 'bg-slate-900 text-white font-semibold'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          {t.label}
          {t.to === '/notifications' && hasUnread && (
            <span className="absolute top-1 right-1.5 w-1.5 h-1.5 rounded-full bg-rose-500" />
          )}
        </Link>
      ))}
    </div>
  );
}
